import React, { useEffect, useState, useCallback, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient, { formatApiError } from '../utils/api';
import * as PhosphorIcons from '@phosphor-icons/react';
import { toast } from 'sonner';
import SetupWizard from '../pages/SetupWizard';

const {
  House, Storefront: StorefrontSimple, FolderOpen, MusicNotes: MusicNotesSimple,
  Queue, Clock, Calendar: CalendarDots, Megaphone, FileText, SignOut, User,
  CreditCard, Users: UsersIcon, ShieldCheck, ChartBar, Eye, ArrowLeft, Palette, Compass,
  ChatCircle, Envelope, Bell, X, Rocket, Question
} = PhosphorIcons;

import ExpiryBanner from './ExpiryBanner';
import { Button } from './ui/button';

const DashboardLayout = ({ children }) => {
  const { user, logout, impersonating, exitImpersonation } = useAuth();
  const navigate = useNavigate();

  // First-time onboarding — block tenant_admin dashboard until they go through
  // the setup wizard OR explicitly skip. Only runs for real tenant admins,
  // not for global admins or impersonated sessions (which have their own UX).
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupChecked, setSetupChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (setupChecked) return;
      if (!user || user.role !== 'tenant_admin') { setSetupChecked(true); return; }
      if (impersonating) { setSetupChecked(true); return; }
      if (user.tenant_status === 'pending_payment' || user.tenant_status === 'blocked') {
        setSetupChecked(true); return;
      }
      try {
        const res = await apiClient.get('/tenant/settings');
        if (!cancelled && res.data?.setup_completed !== true) setShowSetupModal(true);
      } catch (e) { /* non-fatal */ }
      finally { if (!cancelled) setSetupChecked(true); }
    };
    check();
    return () => { cancelled = true; };
  }, [user, impersonating, setupChecked]);

  const dismissSetup = async () => {
    setShowSetupModal(false);
    try { await apiClient.patch('/tenant/settings', { setup_completed: true }); }
    catch (e) { /* skip is best-effort */ }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const handleExitImpersonation = async () => {
    const result = await exitImpersonation();
    if (result.success) { toast.success('Returned to admin view'); navigate('/dashboard/admin'); }
    else { toast.error(result.error); }
  };

  const menuItems = [
    { path: '/dashboard/', icon: House, label: 'Dashboard', roles: ['tenant_admin', 'manager', 'global_super_admin'] },
    { path: '/dashboard/stores', icon: StorefrontSimple, label: 'Stores', roles: ['tenant_admin'] },
    { path: '/dashboard/groups', icon: FolderOpen, label: 'Groups', roles: ['tenant_admin'] },
    { path: '/dashboard/library', icon: MusicNotesSimple, label: 'Music Library', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/playlists', icon: Queue, label: 'Playlists', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/dayparting', icon: Clock, label: 'Dayparting', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/calendar', icon: CalendarDots, label: 'Calendar', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/announcements', icon: Megaphone, label: 'Announcements', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/users', icon: UsersIcon, label: 'Team', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/analytics', icon: ChartBar, label: 'Analytics', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/subscription', icon: CreditCard, label: 'Subscription', roles: ['tenant_admin'] },
    { path: '/dashboard/legal', icon: FileText, label: 'Legal', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/branding', icon: Palette, label: 'Kiosk Branding', roles: ['tenant_admin'] },
    { path: '/dashboard/setup', icon: Compass, label: 'Setup Guide', roles: ['tenant_admin'] },
    { path: '/dashboard/feedback', icon: ChatCircle, label: 'Feedback', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/help', icon: Question, label: 'Help', roles: ['tenant_admin', 'manager'] },
    { path: '/dashboard/admin', icon: ShieldCheck, label: 'Admin Panel', roles: ['global_super_admin'] },
    { path: '/dashboard/admin/feedback', icon: Envelope, label: 'Feedback Inbox', roles: ['global_super_admin'] },
  ];

  const visibleMenuItems = menuItems.filter(item => !item.roles || item.roles.includes(user?.role));

  const isPendingPayment = user?.tenant_status === 'pending_payment' && user?.role !== 'global_super_admin';

  // Permission-based filtering for managers.
  // Feedback is always available to managers (support channel).
  // Any path not explicitly mapped here is denied by default for managers.
  const permissionMap = {
    '/dashboard/': 'dashboard',
    '/dashboard/library': 'library',
    '/dashboard/playlists': 'playlists',
    '/dashboard/dayparting': 'dayparting',
    '/dashboard/calendar': 'calendar',
    '/dashboard/announcements': 'announcements',
    '/dashboard/analytics': 'analytics',
    '/dashboard/legal': 'legal',
  };
  const MANAGER_ALWAYS_ALLOW = new Set(['/dashboard/feedback', '/dashboard/help', '/dashboard/users']);

  const displayedMenuItems = isPendingPayment
    ? visibleMenuItems.filter(item => ['/dashboard/subscription', '/dashboard/legal'].includes(item.path))
    : user?.role === 'manager'
      ? visibleMenuItems.filter(item => {
          if (MANAGER_ALWAYS_ALLOW.has(item.path)) return true;
          const permKey = permissionMap[item.path];
          if (!permKey) return false; // deny by default for managers
          const perms = Array.isArray(user.permissions) ? user.permissions : [];
          return perms.includes(permKey);
        })
      : visibleMenuItems;

  // In-app notifications (tenant-scoped, global_super_admin skipped — uses Admin Panel)
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const isTenantUser = user && user.role !== 'global_super_admin' && user.role !== 'kiosk';

  const fetchNotifs = useCallback(async () => {
    if (!isTenantUser) return;
    try {
      const [list, c] = await Promise.all([
        apiClient.get('/notifications?limit=25'),
        apiClient.get('/notifications/unread-count'),
      ]);
      setNotifs(list.data || []);
      setUnread(c.data?.count || 0);
    } catch (e) { console.error('[notif] fetch failed', e); }
  }, [isTenantUser]);

  useEffect(() => {
    if (!isTenantUser) return;
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, [isTenantUser, fetchNotifs]);

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const markRead = async (id) => {
    try { await apiClient.patch(`/notifications/${id}/read`); fetchNotifs(); } catch (e) { console.error('[notif] mark-read failed', e); }
  };
  const markAllRead = async () => {
    try {
      await apiClient.patch('/notifications/mark-all-read');
      toast.success('All notifications marked as read');
      fetchNotifs();
    } catch (error) { toast.error(formatApiError(error)); }
  };
  const deleteNotif = async (id) => {
    try { await apiClient.delete(`/notifications/${id}`); fetchNotifs(); } catch (e) { console.error('[notif] delete failed', e); }
  };
  const openLink = (n) => {
    markRead(n.id);
    setNotifOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="dashboard-layout">
      <ExpiryBanner />

      {/* First-time setup modal — blocking overlay with a visible Skip CTA.
          Only ever shown once per tenant; dismissing marks setup_completed. */}
      {showSetupModal && (
        <div
          className="fixed inset-0 z-[60] bg-[#2d2a26]/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
          data-testid="setup-wizard-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-[#f5f0e8] border border-[#c5d4c8] rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="flex items-start justify-between px-6 py-4 border-b border-[#c5d4c8]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#7c9082] flex items-center justify-center">
                  <Rocket size={20} weight="bold" className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#2d2a26]">Welcome to MusicSync</h2>
                  <p className="text-xs text-[#6b665c] font-mono">A quick 5-step guide to get your first kiosk playing.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={dismissSetup}
                className="text-xs font-mono text-[#5a6e5f] hover:text-[#2d2a26] underline px-2 py-1"
                data-testid="setup-wizard-skip"
              >
                Skip for now
              </button>
            </div>
            <div className="p-6">
              <SetupWizard />
            </div>
            <div className="px-6 py-3 border-t border-[#c5d4c8] bg-white/60 flex justify-end">
              <button
                type="button"
                onClick={dismissSetup}
                className="text-xs font-mono text-[#5a6e5f] hover:text-[#2d2a26] px-3 py-1"
                data-testid="setup-wizard-done"
              >
                I'll finish later — take me to the dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {impersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between text-sm font-mono" data-testid="impersonation-banner">
          <div className="flex items-center gap-2"><Eye size={16} weight="bold" /><span>Viewing as <strong>{user?.name || user?.email}</strong></span></div>
          <Button onClick={handleExitImpersonation} className="bg-amber-700/30 hover:bg-amber-700/50 text-amber-950 border-0 h-7 px-3 text-xs font-bold" data-testid="exit-impersonation-btn"><ArrowLeft size={14} className="mr-1" />Back to Admin</Button>
        </div>
      )}

      {/* Notifications bell (tenant users only) */}
      {isTenantUser && (
        <div className={`fixed ${impersonating ? 'top-12' : 'top-4'} right-4 z-40`} ref={panelRef}>
          <button
            type="button"
            onClick={() => setNotifOpen(o => !o)}
            className="relative w-10 h-10 rounded-full bg-white border border-[#c5d4c8] shadow-sm hover:bg-[#f5f0e8] transition-colors flex items-center justify-center"
            data-testid="notifications-bell"
          >
            <Bell size={18} className="text-[#5a6e5f]" />
            {unread > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                data-testid="notifications-unread-badge"
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-12 w-80 max-h-[70vh] overflow-y-auto bg-white border border-[#c5d4c8] rounded-lg shadow-lg" data-testid="notifications-panel">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#c5d4c8] sticky top-0 bg-white">
                <h3 className="text-sm font-bold text-[#2d2a26]">Notifications</h3>
                {notifs.length > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-[#5a6e5f] font-mono hover:text-[#3f5246]"
                    data-testid="notifications-mark-all-read"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              {notifs.length === 0 ? (
                <div className="p-6 text-center text-xs text-[#8a8477] font-mono">No notifications yet.</div>
              ) : (
                <div className="divide-y divide-[#e4ebe5]">
                  {notifs.map(n => (
                    <div
                      key={n.id}
                      className={`px-4 py-3 text-sm cursor-pointer transition-colors ${n.read ? 'bg-white' : 'bg-[#f5f0e8]'} hover:bg-[#e4ebe5]`}
                      data-testid={`notification-${n.id}`}
                      onClick={() => openLink(n)}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#2d2a26] truncate">{n.title}</p>
                          <p className="text-xs text-[#6b665c] whitespace-pre-wrap line-clamp-3">{n.message}</p>
                          <p className="text-[10px] text-[#8a8477] font-mono mt-1">{new Date(n.created_at).toLocaleString()}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotif(n.id); }}
                          className="text-[#a39e92] hover:text-red-500 shrink-0"
                          data-testid={`delete-notification-${n.id}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <aside className={`bg-[#e4ebe5] border-r border-[#c5d4c8] p-4 min-h-screen ${impersonating ? 'pt-14' : ''}`} data-testid="dashboard-sidebar">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#7c9082] flex items-center justify-center">
              <MusicNotesSimple size={24} weight="bold" className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-tight text-[#2d2a26]">MusicSync</h2>
              <p className="text-xs text-[#5a6e5f] font-mono">Control Center</p>
            </div>
          </div>
        </div>

        <nav className="space-y-0.5">
          {isPendingPayment && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
              <p className="text-xs text-amber-700 font-medium">Payment required</p>
              <p className="text-[10px] text-amber-600 mt-0.5">Complete payment to unlock all features</p>
            </div>
          )}
          {displayedMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.path} to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                    isActive ? 'bg-white/80 text-[#5a6e5f] font-medium shadow-sm border border-[#c5d4c8]' : 'text-[#5a6e5f] hover:bg-white/50 hover:text-[#2d2a26] border border-transparent'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                end={item.path === '/dashboard/'}
              >
                <Icon size={20} weight="regular" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto pt-8 border-t border-[#c5d4c8]">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${impersonating ? 'bg-amber-100 border-amber-400' : 'bg-white border-[#c5d4c8]'}`}>
              <User size={16} className={impersonating ? 'text-amber-600' : 'text-[#7c9082]'} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#2d2a26] truncate">{user?.name}</p>
              <p className="text-xs text-[#5a6e5f] font-mono">{impersonating ? 'impersonating' : user?.role}</p>
            </div>
          </div>
          <Button onClick={handleLogout} className="w-full bg-white hover:bg-[#f0f4f1] text-[#5a6e5f] border border-[#c5d4c8] transition-all duration-200 justify-start" data-testid="logout-button">
            <SignOut size={20} className="mr-2" />Logout
          </Button>
        </div>
      </aside>

      <main className={`bg-[#f5f0e8] p-6 ${impersonating ? 'pt-16' : ''}`} data-testid="dashboard-main-content">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
